// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { supabase } from '../supabase';
import { ArrowLeft, Mail, Send } from 'lucide-react';

export default function ForgotPasswordPage({ onBack }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) { setError('Please enter your email address'); return; }
    setLoading(true);
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + '/reset-password',
      });
      if (resetError) throw resetError;
      setSent(true);
    } catch (err) {
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0F766E 0%, #0D6E66 50%, #134E4A 100%)',
      padding: 16,
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 20,
        padding: '40px 36px',
        width: '100%',
        maxWidth: 400,
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>{sent ? '✉️' : '🔐'}</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#0F766E' }}>
            {sent ? 'Check Your Email' : 'Reset Password'}
          </div>
          <div style={{ fontSize: 13, color: '#64748B', marginTop: 4 }}>
            {sent
              ? 'We sent a password reset link'
              : 'Enter your email to receive a reset link'
            }
          </div>
        </div>

        {error && (
          <div style={{
            background: '#FEF2F2', color: '#DC2626', fontSize: 13,
            padding: '10px 14px', borderRadius: 10, marginBottom: 16, textAlign: 'center',
          }}>
            {error}
          </div>
        )}

        {sent ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: 14, color: '#475569', lineHeight: 1.6, marginBottom: 20,
            }}>
              We sent a password reset link to <strong style={{ color: '#0F766E' }}>{email}</strong>.
              Click the link in the email to reset your password.
            </div>
            <div style={{
              fontSize: 12, color: '#64748B', marginBottom: 20,
              padding: 12, background: '#F0FDF9', borderRadius: 10,
              border: '1px solid #CCFBF1',
            }}>
              Didn't get it? Check your spam folder or wait a minute and try again.
            </div>
            <button
              onClick={() => { setSent(false); setEmail(''); }}
              style={{
                width: '100%', padding: '13px 24px',
                background: 'linear-gradient(135deg, #0F766E, #0D6E66)',
                color: '#fff', border: 'none', borderRadius: 12,
                fontSize: 15, fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                boxShadow: '0 4px 14px rgba(15,118,110,0.35)',
                marginBottom: 12,
              }}
            >
              <Send size={18} /> Try Another Email
            </button>
            <button
              onClick={onBack}
              style={{
                width: '100%', padding: '11px 24px',
                background: 'transparent', color: '#0F766E', border: '1px solid #CCFBF1',
                borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}
            >
              <ArrowLeft size={16} /> Back to Login
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748B', marginBottom: 6 }}>
                Email Address
              </label>
              <div style={{ position: 'relative' }}>
                <Mail size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }} />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@store.com"
                  required
                  autoFocus
                  style={{
                    width: '100%', padding: '11px 14px 11px 40px', border: '1px solid #CCFBF1',
                    borderRadius: 10, fontSize: 14, outline: 'none', fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                  onFocus={e => { e.target.style.borderColor = '#14B8A6'; e.target.style.boxShadow = '0 0 0 3px rgba(20,184,166,0.12)'; }}
                  onBlur={e => { e.target.style.borderColor = '#CCFBF1'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '13px 24px',
                background: loading ? '#99F6E4' : 'linear-gradient(135deg, #0F766E, #0D6E66)',
                color: '#fff', border: 'none', borderRadius: 12,
                fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                boxShadow: loading ? 'none' : '0 4px 14px rgba(15,118,110,0.35)',
                transition: 'all 0.2s',
                marginBottom: 12,
              }}
            >
              {loading ? (
                <>
                  <div style={{ width: 18, height: 18, border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  Sending...
                </>
              ) : (
                <><Send size={18} /> Send Reset Link</>
              )}
            </button>

            <button
              type="button"
              onClick={onBack}
              style={{
                width: '100%', padding: '11px 24px',
                background: 'transparent', color: '#0F766E', border: '1px solid #CCFBF1',
                borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}
            >
              <ArrowLeft size={16} /> Back to Login
            </button>
          </form>
        )}

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 12, color: '#94A3B8' }}>
          Contact your store manager if you need help.
        </div>
      </div>
    </div>
  );
}
